// src/components/ArtworkAgreementEditorModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AgreementAcceptModal from "./AgreementAcceptModal";
import { buildAgreementFormTerms } from "../lib/hostingAgreementViewModel";

const RESPONSIBILITY_OPTIONS = ["HOST", "ARTIST"];

const formatStatusUpdatedAt = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const normalizeArtwork = (agreement) => agreement?.artwork || {};

const getArtworkTitle = (artwork) => artwork?.title || "Untitled";

const getArtistName = (agreement) => {
  const artist = agreement?.artist;
  if (!artist) return "-";
  return artist.fullName || [artist.user?.first_name, artist.user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || artist.name || "-";
};

const getHostName = (agreement) => {
  const host = agreement?.host;
  if (!host) return "-";
  return host.fullName || [host.user?.first_name, host.user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || host.name || "-";
};

export default function ArtworkAgreementEditorModal({
  open,
  onClose,
  agreement,
  mode = "respond",
  onAccept,
  onReject,
  onCounteroffer,
  submitting: controlledSubmitting = false,
}) {
  const [form, setForm] = useState(buildAgreementFormTerms(agreement));
  const [initialForm, setInitialForm] = useState(buildAgreementFormTerms(agreement));
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [success, setSuccess] = useState("");
  const submitting = localSubmitting || controlledSubmitting;

  useEffect(() => {
    if (!open) return;
    const normalized = buildAgreementFormTerms(agreement);
    setForm(normalized);
    setInitialForm(normalized);
    setError("");
    setSuccess("");
  }, [open, agreement]);
  const activeAgreement = agreement;

  const commissionTotal = useMemo(() => {
    const artist = Number(form.commission_artist);
    const host = Number(form.commission_host);
    if (Number.isNaN(artist) || Number.isNaN(host)) return null;
    return artist + host;
  }, [form.commission_artist, form.commission_host]);

  const commissionValid = commissionTotal === 100;
  const datesValid = Boolean(form.start_date && form.end_date)
    && new Date(form.start_date) <= new Date(form.end_date);
  const priceProvided = String(form.artwork_price).trim() !== "";
  const priceValue = Number(form.artwork_price);
  const priceValid = priceProvided && Number.isFinite(priceValue) && priceValue >= 0;
  const requiredValid = Boolean(
    form.insurance_responsibility &&
    form.shipping_to_host_responsibility &&
    form.shipping_from_host_responsibility
  );
  const formValid = commissionValid && datesValid && requiredValid && priceValid;

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialForm), [form, initialForm]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const toCommissionInt = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return num;
    return num <= 1 ? Math.round(num * 100) : Math.round(num);
  };

  const buildTermsPayload = () => ({
    commission_artist: toCommissionInt(form.commission_artist),
    commission_host: toCommissionInt(form.commission_host),
    insurance_responsibility: form.insurance_responsibility,
    shipping_to_host_responsibility: form.shipping_to_host_responsibility,
    shipping_from_host_responsibility: form.shipping_from_host_responsibility,
    start_date: form.start_date,
    end_date: form.end_date,
    artwork_price: Number(form.artwork_price),
    price: Number(form.artwork_price),
  });

  const normalizeForComparison = (terms) => ({
    commission_artist: Number(terms.commission_artist),
    commission_host: Number(terms.commission_host),
    insurance_responsibility: terms.insurance_responsibility || "",
    shipping_to_host_responsibility: terms.shipping_to_host_responsibility || "",
    shipping_from_host_responsibility: terms.shipping_from_host_responsibility || "",
    start_date: terms.start_date || "",
    end_date: terms.end_date || "",
    price: Number(terms.price ?? terms.artwork_price),
  });

  const handleCounteroffer = async () => {
    if (!agreement?.id || !formValid) return;
    setLocalSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const nextTerms = buildTermsPayload();
      const oldNormalized = normalizeForComparison({
        commission_artist: initialForm.commission_artist,
        commission_host: initialForm.commission_host,
        insurance_responsibility: initialForm.insurance_responsibility,
        shipping_to_host_responsibility: initialForm.shipping_to_host_responsibility,
        shipping_from_host_responsibility: initialForm.shipping_from_host_responsibility,
        start_date: initialForm.start_date,
        end_date: initialForm.end_date,
        artwork_price: initialForm.artwork_price,
        price: initialForm.artwork_price,
      });
      const nextNormalized = normalizeForComparison(nextTerms);

      if (mode === "changeTerms") {
        if (JSON.stringify(oldNormalized) === JSON.stringify(nextNormalized)) {
          setError("No terms were changed. Please update at least one value before submitting.");
          return;
        }
        await onCounteroffer?.(agreement.id, nextTerms, {
          mode,
          artworkId: agreement?.artwork?.id || null,
          originalAgreementId: agreement.id,
          hostId: agreement?.host?.id || null,
        });
        setSuccess("New offer submitted.");
      } else {
        await onCounteroffer?.(agreement.id, nextTerms, {
          mode,
          artworkId: agreement?.artwork?.id || null,
          originalAgreementId: agreement.id,
          hostId: agreement?.host?.id || null,
        });
        setSuccess("Counteroffer sent.");
      }
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 409
          ? "This agreement is not awaiting your response. Refreshing..."
          : err?.response?.data?.detail || "Failed to send counteroffer."
      );
    } finally {
      setLocalSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!agreement?.id) return;
    setLocalSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await onReject?.(agreement.id);
      setSuccess("Request rejected.");
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 409
          ? "This agreement is not awaiting your response. Refreshing..."
          : err?.response?.data?.detail || "Failed to reject agreement."
      );
    } finally {
      setLocalSubmitting(false);
      setConfirmRejectOpen(false);
    }
  };

  const handleAgreementSigned = async () => {
    if (!agreement?.id) return;
    setLocalSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await onAccept?.(agreement.id);
      setSuccess("Signed.");
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 409
          ? "This agreement is not awaiting your response. Refreshing..."
          : err?.response?.data?.detail || "Failed to accept agreement."
      );
      throw err;
    } finally {
      setLocalSubmitting(false);
    }
  };

  const metadata = {
    revision: activeAgreement?.revision ?? "-",
    lastProposedBy: activeAgreement?.lastProposedBy ?? activeAgreement?.last_proposed_by ?? activeAgreement?.last_proposed_by_display ?? "-",
    counterpartyDue: activeAgreement?.counterpartyDue ?? activeAgreement?.counterparty_due ?? "-",
    termsHash: activeAgreement?.termsHash ?? activeAgreement?.terms_hash ?? "-",
  };

  const artwork = normalizeArtwork(activeAgreement);
  const artworkTitle = getArtworkTitle(artwork);
  const hostName = getHostName(activeAgreement);
  const statusUpdatedAtLabel = formatStatusUpdatedAt(activeAgreement?.updatedAt || activeAgreement?.updated_at);

  return (
    <>
      <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {mode === "changeTerms" ? "Change Agreement Terms" : "Artwork Hosting Agreement"}
          {activeAgreement?.status && (
            <>
              <Chip label={activeAgreement.status} size="small" sx={{ ml: 1 }} />
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                at {statusUpdatedAtLabel}
              </Typography>
            </>
          )}
          {mode !== "changeTerms" && activeAgreement?.status && (
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {artworkTitle} {activeAgreement.status} at {statusUpdatedAtLabel}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Typography variant="subtitle2">Artwork</Typography>
            <Typography variant="body2">
              {artworkTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Host: {hostName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Artist: {getArtistName(activeAgreement)}
            </Typography>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Artist Commission (%)"
                type="number"
                value={form.commission_artist}
                onChange={handleChange("commission_artist")}
                inputProps={{ min: 0, max: 100, step: 1 }}
                fullWidth
                error={!commissionValid}
                helperText={commissionValid ? "" : "Artist + Host must total 100%."}
              />
              <TextField
                label="Host Commission (%)"
                type="number"
                value={form.commission_host}
                onChange={handleChange("commission_host")}
                inputProps={{ min: 0, max: 100, step: 1 }}
                fullWidth
                error={!commissionValid}
                helperText={commissionValid ? "" : "Artist + Host must total 100%."}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="agreement-insurance-label">Insurance Responsibility</InputLabel>
              <Select
                labelId="agreement-insurance-label"
                label="Insurance Responsibility"
                value={form.insurance_responsibility}
                onChange={handleChange("insurance_responsibility")}
              >
                {RESPONSIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="agreement-shipping-to-label">Shipping to Host</InputLabel>
              <Select
                labelId="agreement-shipping-to-label"
                label="Shipping to Host"
                value={form.shipping_to_host_responsibility}
                onChange={handleChange("shipping_to_host_responsibility")}
              >
                {RESPONSIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="agreement-shipping-from-label">Shipping from Host</InputLabel>
              <Select
                labelId="agreement-shipping-from-label"
                label="Shipping from Host"
                value={form.shipping_from_host_responsibility}
                onChange={handleChange("shipping_from_host_responsibility")}
              >
                {RESPONSIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Artwork Price"
              type="number"
              value={form.artwork_price}
              onChange={handleChange("artwork_price")}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              error={!priceValid}
              helperText={!priceValid ? "Price is required and must be 0 or greater." : ""}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={form.start_date}
                onChange={handleChange("start_date")}
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!datesValid && Boolean(form.start_date || form.end_date)}
                helperText={!datesValid && Boolean(form.start_date || form.end_date) ? "Start date must be before end date." : ""}
              />
              <TextField
                label="End Date"
                type="date"
                value={form.end_date}
                onChange={handleChange("end_date")}
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={!datesValid && Boolean(form.start_date || form.end_date)}
                helperText={!datesValid && Boolean(form.start_date || form.end_date) ? "End date must be after start date." : ""}
              />
            </Stack>

            {!requiredValid && (
              <FormHelperText>Select all responsibility fields before proceeding.</FormHelperText>
            )}

            {isDirty && (
              <Alert severity="info">
                You have unsent edits. Submit a new offer to send changes, or revert before signing.
              </Alert>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Proposal Metadata
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">Revision: {metadata.revision}</Typography>
                <Typography variant="body2">Last proposed by: {metadata.lastProposedBy}</Typography>
                <Typography variant="body2">Counterparty due: {metadata.counterpartyDue}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Terms hash: {metadata.termsHash}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {mode !== "changeTerms" && (
              <Button
                color="error"
                onClick={() => setConfirmRejectOpen(true)}
                disabled={submitting}
              >
                Reject
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleCounteroffer}
              disabled={!formValid || submitting}
            >
              {mode === "changeTerms" ? "Submit New Offer" : "Counteroffer"}
            </Button>
          </Box>
          {mode !== "changeTerms" && (
            <Button
              variant="contained"
              onClick={() => setAcceptOpen(true)}
              disabled={!formValid || isDirty || submitting}
            >
              Agree &amp; Sign
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {mode !== "changeTerms" && <Dialog open={confirmRejectOpen} onClose={() => setConfirmRejectOpen(false)}>
        <DialogTitle>Reject Host Request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will reject the current offer. The other party will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRejectOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={submitting}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>}

      {mode !== "changeTerms" && <AgreementAcceptModal
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        agreementCode="ARTWORK_HOSTING"
        title="Artwork Hosting Agreement"
        workflowContext={{ workflow: "artwork_hosting" }}
        onAccepted={handleAgreementSigned}
      />}
    </>
  );
}
